/**
 * Browser logger — a console shim satisfying the same `Logger` seam.
 *
 * Exists so pino (and node:* built-ins) never enter a web bundle, and so a
 * production SPA does not narrate itself in the user's console. The single
 * sanctioned `console` writer in the repo; `no-console` is an ESLint error
 * everywhere else and this file is the one exemption.
 */
import type { LogFields, Logger } from "./types";

/** Dev: everything. Prod: warn and error only (levels are indexes 0..4). */
const MIN_LEVEL = import.meta.env?.DEV ? 0 : 3;

const make = (bindings: LogFields): Logger => {
  const emit =
    (level: number, method: "debug" | "info" | "warn" | "error") =>
    (fields: LogFields, msg?: string) => {
      if (level < MIN_LEVEL) return;
      // No inline eslint-disable needed: the root eslint.config.js exempts this
      // one file from `no-console`, which keeps the exemption reviewable in one
      // place instead of scattered per line.
      console[method](msg ?? "", { ...bindings, ...fields });
    };

  return {
    trace: emit(0, "debug"),
    debug: emit(1, "debug"),
    info: emit(2, "info"),
    warn: emit(3, "warn"),
    error: emit(4, "error"),
    child: (extra) => make({ ...bindings, ...extra }),
  };
};

/** Browser-side root logger. Same call shape as the server one. */
export const logger = make({});

export type { LogFields, Logger } from "./types";
